/*
 * UpPromote Affiliate Link Rewriter & Badge Renderer
 *
 * When the page URL contains ?sca_ref=<id>.<suffix>:
 *   1. Parse <id> (numeric) and <suffix>.
 *   2. Resolve <id> → {program_id, first_name, last_name} via
 *      /apps/voltawellcrm-api/get-affiliate (Shopify proxy → Yii2 →
 *      UpPromote; the UpPromote API key never reaches the browser).
 *   3. Rewrite every <a> on the page whose host is ambassador.voltawell.com
 *      to append ?ref=<suffix>&p=<program_id>.
 *   4. Insert a pill badge with the affiliate's "First Last" immediately
 *      before each rewritten anchor (only when name fields are present).
 *   5. Persist the resolved data to a 90-day cookie so subsequent pageviews
 *      skip the API call. A different ?sca_ref overrides the cookie.
 *
 * Failure handling:
 *   - If the API call fails on a fresh sca_ref, stash the raw value in
 *     sessionStorage and retry on the next pageview. If a cached cookie
 *     exists, use it as a fallback so links keep working in the meantime.
 *   - A MutationObserver re-runs the rewrite/decorate on DOM changes so
 *     AJAX-injected content (cart drawer, etc.) gets the same treatment.
 *
 * Cookie upgrade path:
 *   - Old cookies created before this version don't carry first_name /
 *     last_name. We detect that on read and trigger a one-shot refresh
 *     against the API to populate the missing fields without forcing
 *     visitors to revisit the original sca_ref link.
 */
(function () {
  'use strict';

  var AMBASSADOR_HOSTS = ['ambassador.voltawell.com', 'www.ambassador.voltawell.com'];
  var COOKIE_NAME = 'vw_sca_ref';
  var COOKIE_DAYS = 90;
  var PENDING_KEY = 'vw_sca_ref_pending';
  var API_PATH = '/apps/voltawellcrm-api/get-affiliate';
  var BADGE_CLASS = 'vw-affiliate-badge';
  var WRAP_CLASS = 'vw-affiliate-wrap';   // legacy — only used to clean up previous wrapper structure
  var STYLE_FLAG = '__vwAffiliateStyleInjected__';
  // Below this viewport width, mobile-style float-right kicks in. 992px
  // matches Bootstrap's `lg` breakpoint, which is where the theme's nav
  // collapses to the hamburger / list-group menu.
  var MOBILE_BREAKPOINT_PX = 992;

  function parseScaRef(value) {
    if (!value || typeof value !== 'string') return null;
    var dot = value.indexOf('.');
    if (dot < 1 || dot === value.length - 1) return null;
    var id = value.slice(0, dot);
    var suffix = value.slice(dot + 1);
    if (!/^\d+$/.test(id)) return null;
    return { id: id, suffix: suffix, raw: value };
  }

  function readCookie(name) {
    var prefix = name + '=';
    var parts = document.cookie ? document.cookie.split(';') : [];
    for (var i = 0; i < parts.length; i++) {
      var c = parts[i].replace(/^\s+/, '');
      if (c.indexOf(prefix) === 0) {
        try { return decodeURIComponent(c.slice(prefix.length)); }
        catch (e) { return null; }
      }
    }
    return null;
  }

  function writeCookie(name, value, days) {
    var expires = '';
    if (days) {
      var d = new Date();
      d.setTime(d.getTime() + days * 86400 * 1000);
      expires = '; expires=' + d.toUTCString();
    }
    document.cookie = name + '=' + encodeURIComponent(value) + expires
      + '; path=/; SameSite=Lax';
  }

  function readJSON(raw) {
    if (!raw) return null;
    try { return JSON.parse(raw); } catch (e) { return null; }
  }

  function readPending() {
    try { return sessionStorage.getItem(PENDING_KEY); } catch (e) { return null; }
  }
  function setPending(raw) {
    try { sessionStorage.setItem(PENDING_KEY, raw); } catch (e) { /* private mode */ }
  }
  function clearPending() {
    try { sessionStorage.removeItem(PENDING_KEY); } catch (e) { /* private mode */ }
  }

  function fetchAffiliate(id) {
    return fetch(API_PATH + '?id=' + encodeURIComponent(id), {
      method: 'GET',
      headers: { 'Accept': 'application/json' },
      credentials: 'same-origin'
    }).then(function (res) {
      if (!res.ok) return null;
      return res.json();
    }).then(function (data) {
      if (!data || !data.success || !data.program_id) return null;
      var pid = parseInt(data.program_id, 10);
      if (isNaN(pid)) return null;
      return {
        program_id: pid,
        first_name: typeof data.first_name === 'string' ? data.first_name.trim() : '',
        last_name: typeof data.last_name === 'string' ? data.last_name.trim() : ''
      };
    }).catch(function () {
      return null;
    });
  }

  function isAmbassadorHost(host) {
    for (var i = 0; i < AMBASSADOR_HOSTS.length; i++) {
      if (AMBASSADOR_HOSTS[i] === host) return true;
    }
    return false;
  }

  function injectStylesOnce() {
    if (window[STYLE_FLAG]) return;
    window[STYLE_FLAG] = true;
    var css = ''
      // The badge is appended inside the ambassador <a>, so it sits within
      // whatever bordered area the link defines (e.g. .list-group-item).
      // Default — desktop nav: inline-block to the right of the link text.
      + '.' + BADGE_CLASS + ' {'
      +   'display: inline-block;'
      +   'margin-left: 0.5rem;'
      +   'padding: 0.2rem 0.6rem;'
      +   'font-size: 0.75rem;'
      +   'font-weight: 600;'
      +   'line-height: 1.3;'
      +   'color: #0f172a;'
      +   'background: linear-gradient(135deg, #cffafe 0%, #dbeafe 100%);'
      +   'border-radius: 9999px;'
      +   'letter-spacing: 0.01em;'
      +   'white-space: nowrap;'
      +   'vertical-align: middle;'
      +   'pointer-events: none;'
      + '}'
      // Mobile / collapsed nav (Bootstrap lg breakpoint): pin the badge to
      // the right edge of the link's content box — works regardless of
      // whether the link's display is block or flex.
      + '@media (max-width: ' + (MOBILE_BREAKPOINT_PX - 0.02) + 'px) {'
      +   '.' + BADGE_CLASS + ' {'
      +     'float: right;'
      +     'margin-left: 0;'
      +     'margin-top: 0.1rem;'
      +   '}'
      + '}';
    var style = document.createElement('style');
    style.setAttribute('data-vw-affiliate-styles', '');
    style.appendChild(document.createTextNode(css));
    document.head.appendChild(style);
  }

  function buildBadge(name) {
    var span = document.createElement('span');
    span.className = BADGE_CLASS;
    span.textContent = name;
    return span;
  }

  // Idempotent badge insert/refresh inside the link itself.
  function decorateBadge(a, name) {
    var existingBadge = a.querySelector('.' + BADGE_CLASS);
    if (existingBadge) {
      if (existingBadge.textContent !== name) existingBadge.textContent = name;
    } else {
      a.appendChild(buildBadge(name));
    }
  }

  function rewriteAndDecorate(active) {
    if (!active || !active.suffix || !active.program_id) return;
    var suffix = active.suffix;
    var programId = active.program_id;
    var name = ((active.first_name || '') + ' ' + (active.last_name || '')).trim();
    var hasName = name.length > 0;

    if (hasName) injectStylesOnce();

    var anchors = document.querySelectorAll('a[href]');
    for (var i = 0; i < anchors.length; i++) {
      var a = anchors[i];
      var href = a.getAttribute('href');
      if (!href) continue;

      // Links explicitly marked to carry the affiliate name badge even though
      // they no longer point at the ambassador host — e.g. the nav
      // "Ambassadors" item once it links to the on-site /pages/ambassador
      // page. These get the badge only; no partner params (they're internal).
      var marked = a.hasAttribute('data-vw-affiliate-badge');

      try {
        var u = new URL(href, window.location.href);

        if (!isAmbassadorHost(u.host)) {
          if (marked && hasName) decorateBadge(a, name);
          continue;
        }

        // Ambassador-host link: append partner params on any page.
        u.searchParams.set('ref', suffix);
        u.searchParams.set('p', String(programId));
        a.href = u.toString();

        if (!hasName) continue;

        // Opt-out: host links that should receive params but stay visually
        // clean (e.g. the page's "Become an Ambassador" CTA button).
        if (a.hasAttribute('data-vw-affiliate-no-badge')) continue;

        // Migration: if a previous version wrapped the link in a
        // .vw-affiliate-wrap, unwrap it before placing the badge inside
        // the link. (No-op for fresh page loads; only matters during a
        // brief window after a JS deploy where cached older code may
        // have run before this version replaced it.)
        var parent = a.parentNode;
        if (parent && parent.classList && parent.classList.contains(WRAP_CLASS)) {
          var grandparent = parent.parentNode;
          if (grandparent) {
            grandparent.insertBefore(a, parent);
            grandparent.removeChild(parent);
          }
        }

        decorateBadge(a, name);
      } catch (e) { /* ignore malformed hrefs */ }
    }
  }

  function applyAndObserve(active) {
    if (!active || !active.program_id) return;
    rewriteAndDecorate(active);

    if (!('MutationObserver' in window)) return;
    var observer = new MutationObserver(function () {
      rewriteAndDecorate(active);
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  function activeFromIncomingAndResolved(incoming, resolved) {
    return {
      id: incoming.id,
      suffix: incoming.suffix,
      raw: incoming.raw,
      program_id: resolved.program_id,
      first_name: resolved.first_name,
      last_name: resolved.last_name
    };
  }

  function run() {
    var params;
    try { params = new URLSearchParams(window.location.search); }
    catch (e) { params = null; }

    var incoming = params ? parseScaRef(params.get('sca_ref')) : null;
    var cached = readJSON(readCookie(COOKIE_NAME));
    var pending = parseScaRef(readPending());

    if (incoming) {
      // Cache is reusable only when raw matches AND it carries the name
      // fields (older cookies don't, and we want to backfill them).
      var cacheValid = cached
        && cached.raw === incoming.raw
        && cached.program_id
        && typeof cached.first_name === 'string';
      if (cacheValid) {
        applyAndObserve(cached);
        return;
      }
      fetchAffiliate(incoming.id).then(function (resolved) {
        if (resolved) {
          var active = activeFromIncomingAndResolved(incoming, resolved);
          writeCookie(COOKIE_NAME, JSON.stringify(active), COOKIE_DAYS);
          clearPending();
          applyAndObserve(active);
        } else {
          // Resolution failed; stash for next-pageview retry. Fall back to
          // any cached affiliate so links keep working in the meantime.
          setPending(incoming.raw);
          if (cached && cached.program_id) applyAndObserve(cached);
        }
      });
      return;
    }

    if (pending) {
      fetchAffiliate(pending.id).then(function (resolved) {
        if (resolved) {
          var active = activeFromIncomingAndResolved(pending, resolved);
          writeCookie(COOKIE_NAME, JSON.stringify(active), COOKIE_DAYS);
          clearPending();
          applyAndObserve(active);
        } else if (cached && cached.program_id) {
          applyAndObserve(cached);
        }
      });
      return;
    }

    if (cached && cached.program_id) {
      // Cookie predates the name fields — backfill via one-shot refresh so
      // the badge appears on this visit. Doesn't block link rewriting if
      // the refresh fails.
      if (typeof cached.first_name !== 'string') {
        fetchAffiliate(cached.id).then(function (resolved) {
          if (resolved) {
            var refreshed = {
              id: cached.id,
              suffix: cached.suffix,
              raw: cached.raw,
              program_id: resolved.program_id,
              first_name: resolved.first_name,
              last_name: resolved.last_name
            };
            writeCookie(COOKIE_NAME, JSON.stringify(refreshed), COOKIE_DAYS);
            applyAndObserve(refreshed);
          } else {
            applyAndObserve(cached);
          }
        });
        return;
      }
      applyAndObserve(cached);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
})();
